import React, { useCallback, useContext, useMemo, useState } from "react";

import { ItemDataType } from "components/Tree/types";

export const LeftSidebarContext = React.createContext<{
  databaseSearchState: string;
  engineSearchState: string;
  securitySearchState: string;
  expandedItems: string[];
  treeData: ItemDataType[];
  onDatabaseSearchChange: (value: string) => void;
  onEngineSearchChange: (value: string) => void;
  onSecuritySearchChange: (value: string) => void;
  onExpand: (value: string[]) => void;
  preserveTreeData: (treeData: ItemDataType[]) => void;
  setWidth: (width: number) => void;
}>({
  databaseSearchState: "",
  engineSearchState: "",
  securitySearchState: "",
  expandedItems: [],
  treeData: [],
  onDatabaseSearchChange: () => {},
  onEngineSearchChange: () => {},
  onSecuritySearchChange: () => {},
  onExpand: () => {},
  preserveTreeData: () => {},
  setWidth: () => {},
});

export const useLeftSidebarState = ({
  setWidth,
}: {
  setWidth: (width: number) => void;
}) => {
  const [databaseSearchState, setDatabasesSearchState] = useState("");
  const [engineSearchState, setEngineSearchState] = useState("");
  const [securitySearchState, setSecuritySearchState] = useState("");
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [treeData, setTreeData] = useState<ItemDataType[]>([]);

  const onDatabaseSearchChange = useCallback((value: string) => {
    setDatabasesSearchState(value);
  }, []);

  const onEngineSearchChange = useCallback((value: string) => {
    setEngineSearchState(value);
  }, []);

  const onSecuritySearchChange = useCallback((value: string) => {
    setSecuritySearchState(value);
  }, []);

  const onExpand = useCallback(
    (nextExpandedItems: string[]) => setExpandedItems(nextExpandedItems),
    []
  );

  const preserveTreeData = useCallback((treeData: ItemDataType[]) => {
    setTreeData(treeData);
  }, []);

  const leftSidebarContext = useMemo(() => {
    return {
      engineSearchState,
      databaseSearchState,
      securitySearchState,
      onDatabaseSearchChange,
      onEngineSearchChange,
      onSecuritySearchChange,
      expandedItems,
      setExpandedItems,
      onExpand,
      preserveTreeData,
      treeData,
      setWidth,
    };
  }, [
    databaseSearchState,
    engineSearchState,
    securitySearchState,
    onDatabaseSearchChange,
    onEngineSearchChange,
    onSecuritySearchChange,
    expandedItems,
    onExpand,
    preserveTreeData,
    treeData,
    setWidth,
  ]);

  return {
    leftSidebarContext,
  };
};

export const useLeftSidebarContext = () => {
  const context = useContext(LeftSidebarContext);
  return context;
};
